import { Link } from 'react-router-dom'

interface QuickActionCardProps {
  icon: React.ReactNode
  label: string
  count?: number
  to: string
  color: 'blue' | 'green' | 'purple' | 'orange'
  isPrimary?: boolean
}

const colorClasses = {
  blue: {
    bg: 'bg-blue-50 hover:bg-blue-100',
    icon: 'bg-blue-100 text-blue-600',
    text: 'text-blue-900',
    count: 'text-blue-700',
    border: 'border-blue-200',
    primaryBg: 'bg-blue-600 hover:bg-blue-700',
    primaryText: 'text-white',
  },
  green: {
    bg: 'bg-green-50 hover:bg-green-100',
    icon: 'bg-green-100 text-green-600',
    text: 'text-green-900',
    count: 'text-green-700',
    border: 'border-green-200',
    primaryBg: 'bg-green-600 hover:bg-green-700',
    primaryText: 'text-white',
  },
  purple: {
    bg: 'bg-purple-50 hover:bg-purple-100',
    icon: 'bg-purple-100 text-purple-600',
    text: 'text-purple-900',
    count: 'text-purple-700',
    border: 'border-purple-200',
    primaryBg: 'bg-purple-600 hover:bg-purple-700',
    primaryText: 'text-white',
  },
  orange: {
    bg: 'bg-orange-50 hover:bg-orange-100',
    icon: 'bg-orange-100 text-orange-600',
    text: 'text-orange-900',
    count: 'text-orange-700',
    border: 'border-orange-200',
    primaryBg: 'bg-orange-600 hover:bg-orange-700',
    primaryText: 'text-white',
  },
}

export function QuickActionCard({
  icon,
  label,
  count,
  to,
  color,
  isPrimary = false,
}: QuickActionCardProps) {
  const classes = colorClasses[color]

  if (isPrimary) {
    return (
      <Link
        to={to}
        className={`
          block rounded-xl p-6 transition-all duration-200
          ${classes.primaryBg} ${classes.primaryText}
          shadow-lg hover:shadow-xl transform hover:-translate-y-0.5
        `}
      >
        <div className="flex flex-col items-center text-center gap-3">
          <div className="p-3 rounded-full bg-white/20">{icon}</div>
          <span className="text-lg font-semibold">{label}</span>
        </div>
      </Link>
    )
  }

  return (
    <Link
      to={to}
      className={`
        block rounded-xl border p-6 transition-all duration-200
        ${classes.bg} ${classes.border}
        hover:shadow-md transform hover:-translate-y-0.5
      `}
    >
      <div className="flex flex-col items-center text-center gap-3">
        <div className={`p-3 rounded-full ${classes.icon}`}>{icon}</div>
        {count !== undefined && (
          <span className={`text-3xl font-bold ${classes.count}`}>
            {count.toLocaleString()}
          </span>
        )}
        <span className={`text-sm font-medium ${classes.text}`}>{label}</span>
      </div>
    </Link>
  )
}

// Icons for quick actions
export const QuickActionIcons = {
  buildProgram: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
      />
    </svg>
  ),
  activePrograms: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
      />
    </svg>
  ),
  completedPrograms: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  videoLibrary: (
    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  ),
}
